import type {GuestCategory} from "crqlar-react-common/dist/models/profile/guestCategory.model";
import {Sector} from "crqlar-react-common/dist/models/sector/sector.model";
import {SurveyReviewState} from "crqlar-react-common/dist/models/survey/surveySubmission.model";
import {getNameForLanguage} from "crqlar-react-common/dist/utils/translations.util";
import {SURVEY_REVIEW_STATE_LABEL_KEYS} from "../../common/uiElements/SurveyReviewStateElement";
import type {SurveyEvaluationFilterState} from "./SurveyEvaluationFilters";

type Translate = (key: string, options?: Record<string, unknown>) => string;

export type SurveyFilterId =
    | 'language'
    | 'sector'
    | 'guestCategory'
    | 'reviewState'
    | 'version';

type SurveyFilterOption = {value: string, label: string};

export type SurveyFilterDescriptor = {
    id: SurveyFilterId
    label: string
    options: SurveyFilterOption[]
    valueOf: (state: SurveyEvaluationFilterState) => string | null
    patch: (state: SurveyEvaluationFilterState, value: string | null) => SurveyEvaluationFilterState
};

type SurveyFilterDescriptorInput = {
    languageOptions: string[]
    sectorOptions: Sector[]
    currentVersion?: number
    guestCategories?: GuestCategory[]
    t: Translate
};

export const surveyFilterDescriptors = ({
    languageOptions,
    sectorOptions,
    currentVersion,
    guestCategories = [],
    t
}: SurveyFilterDescriptorInput): SurveyFilterDescriptor[] => {
    const singleSelect = (value: string | null) => (value ? [value] : []);

    const publishedVersions = currentVersion ?? 0;
    const versionOptions = publishedVersions >= 1
        ? Array.from({length: publishedVersions}, (_unused, index) => publishedVersions - index)
        : [];

    return [
        {
            id: 'language',
            label: t('Language'),
            options: languageOptions.map(languageCode => ({
                value: languageCode,
                label: getNameForLanguage(languageCode, true) || languageCode,
            })),
            valueOf: state => state.languages[0] ?? null,
            patch: (state, value) => ({...state, languages: singleSelect(value)}),
        },
        {
            id: 'sector',
            label: t('Sector'),
            options: sectorOptions.map(sector => ({
                value: sector.id!,
                label: sector.name || sector.id || '',
            })),
            valueOf: state => state.sectorIds[0] ?? null,
            patch: (state, value) => ({...state, sectorIds: singleSelect(value)}),
        },
        {
            id: 'guestCategory',
            label: t('Guest category'),
            options: guestCategories.map(category => ({
                value: category.id!,
                label: category.name,
            })),
            valueOf: state => state.guestCategoryId,
            patch: (state, value) => ({...state, guestCategoryId: value}),
        },
        {
            id: 'reviewState',
            label: t('Review state'),
            options: [
                {
                    value: SurveyReviewState.OPEN,
                    label: t(SURVEY_REVIEW_STATE_LABEL_KEYS[SurveyReviewState.OPEN]),
                },
                {
                    value: SurveyReviewState.REVIEWED,
                    label: t(SURVEY_REVIEW_STATE_LABEL_KEYS[SurveyReviewState.REVIEWED]),
                },
            ],
            valueOf: state => state.reviewStates[0] ?? null,
            patch: (state, value) => ({
                ...state,
                reviewStates: singleSelect(value) as SurveyReviewState[],
            }),
        },
        {
            id: 'version',
            label: t('Version'),
            options: versionOptions.map(version => ({
                value: String(version),
                label: version === publishedVersions
                    ? t('v{{version}} (current)', {version})
                    : `v${version}`,
            })),
            valueOf: state => (state.versions[0] != null ? String(state.versions[0]) : null),
            patch: (state, value) => ({...state, versions: value ? [Number(value)] : []}),
        },
    ];
};

// A single guest category is still a choice (that category or everyone); every other filter needs two options.
export const availableSurveyFilters = (descriptors: SurveyFilterDescriptor[]) =>
    descriptors.filter(descriptor => descriptor.options.length >= (descriptor.id === 'guestCategory' ? 1 : 2));

const activeSurveyFilters = (
    descriptors: SurveyFilterDescriptor[],
    state: SurveyEvaluationFilterState
) => descriptors.filter(descriptor => descriptor.valueOf(state) !== null);

const surveyFilterValueLabel = (
    descriptor: SurveyFilterDescriptor,
    state: SurveyEvaluationFilterState
) => {
    const value = descriptor.valueOf(state);
    if (value == null) return null;

    return descriptor.options.find(option => option.value === value)?.label ?? value;
};

export const activeSurveyFilterLines = (
    descriptors: SurveyFilterDescriptor[],
    state: SurveyEvaluationFilterState
) => activeSurveyFilters(descriptors, state).map(descriptor => ({
    label: descriptor.label,
    value: surveyFilterValueLabel(descriptor, state) ?? '',
}));
